
// Centralized permission cache to prevent redundant API calls
class PermissionCache {
  private static instance: PermissionCache;
  private cache = new Map<string, { data: any; timestamp: number }>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private pendingRequests = new Map<string, Promise<any>>();

  public static getInstance(): PermissionCache {
    if (!PermissionCache.instance) {
      PermissionCache.instance = new PermissionCache();
    }
    return PermissionCache.instance;
  }

  public async get<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
    // Check cache first
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      console.log(`🎯 Cache hit for ${key}`);
      return cached.data;
    }

    // Check if request is already pending
    if (this.pendingRequests.has(key)) {
      console.log(`⏳ Waiting for pending request: ${key}`);
      return this.pendingRequests.get(key)!;
    }

    // Make new request
    console.log(`🔄 Cache miss, fetching: ${key}`);
    const promise = fetcher();
    this.pendingRequests.set(key, promise);

    try {
      const data = await promise;
      this.cache.set(key, { data, timestamp: Date.now() });
      return data;
    } finally {
      this.pendingRequests.delete(key);
    }
  }

  public clear(): void {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  public clearUser(userId: number): void {
    const userKeys = Array.from(this.cache.keys()).filter(key => key.includes(`user-${userId}`));
    userKeys.forEach(key => this.cache.delete(key));
  }
}

export const permissionCache = PermissionCache.getInstance();
